import styled from '@emotion/styled';
import { ReactNode, useLayoutEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { privacyPolicyURL } from '../../common/urls';
import {
  isSupportedLocale,
  t,
  useLocale,
} from '../../localization/LocaleContext';
import {
  useModifySupportAccessRightRequest,
  useSupportAccessRightRequest,
} from '../../remoteData/unauthenticated';
import LoadingScreen from '../LoadingScreen';
import { ButtonPrimary, ButtonSecondary } from '../shared/Buttons';
import Checkbox from '../shared/Checkbox';

const Container = styled.div`
  color: ${({ theme }) => theme.colors.blueDark70};
  max-width: 560px;
`;

const Title = styled.h1`
  ${({ theme }) => theme.textStyles.titleL};
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.blueDark100};
  margin-bottom: 21px;

  ${({ theme }) => theme.mediaQuery.md} {
    margin-bottom: ${({ theme }) => theme.spacers.m};
  }
`;

const Paragraph = styled.p`
  ${({ theme }) => theme.textStyles.labelL};

  a {
    color: ${({ theme }) => theme.colors.blueDark100};
    text-decoration: underline;
  }
`;

const CheckboxContainer = styled.div`
  margin-right: 10px;
`;

const CheckboxLabel = styled.label`
  ${({ theme }) => theme.textStyles.labelLEmphasized};
  margin: ${({ theme }) => `${theme.spacers.m} 0 ${theme.spacers.m}`};
  display: flex;
`;

const Email = styled.span`
  color: ${({ theme }) => theme.colors.blueDark100};
`;

const Error = styled.p`
  ${({ theme }) => theme.textStyles.paragraphS};
  color: ${({ theme }) => theme.colors.blueDark50};
  margin-top: ${({ theme }) => theme.spacers.xl};
`;

const RejectButton = styled(ButtonSecondary)`
  margin-top: ${({ theme }) => theme.spacers.xl};
  width: 100%;

  ${({ theme }) => theme.mediaQuery.md} {
    margin-right: ${({ theme }) => theme.spacers.s};
    min-width: 172px;
    width: auto;
  }
`;

const ApproveButton = styled(ButtonPrimary)`
  margin-top: ${({ theme }) => theme.spacers.xl};
  width: 100%;

  ${({ theme }) => theme.mediaQuery.md} {
    min-width: 172px;
    width: auto;
  }
`;

export default function SupportAccessRightRequest() {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale');
  const token = searchParams.get('token') ?? undefined;
  const { selectedLocale, changeLocale } = useLocale();

  useAnonymousAnalyticsTrackPage({
    category: 'Public',
    name: 'Support Access Right Request',
  });

  const { data: supportAccessRightsData, error: supportAccessRightsDataError } =
    useSupportAccessRightRequest(token);
  const {
    mutate: modifyRights,
    error: modifyRightsError,
    data: modifyRightsData,
  } = useModifySupportAccessRightRequest();
  const [consentChecked, setConsentChecked] = useState(false);

  useLayoutEffect(() => {
    if (locale && !Array.isArray(locale) && isSupportedLocale(locale)) {
      changeLocale(locale);
    }
  }, [changeLocale, locale]);

  if (!token || supportAccessRightsDataError) {
    return (
      <Container>
        <Title>{t('support_access_rights_invalid_link_title')}</Title>
        <Paragraph>{t('support_access_rights_invalid_link_text')}</Paragraph>
        {supportAccessRightsDataError && (
          <Error>{supportAccessRightsDataError.message}</Error>
        )}
      </Container>
    );
  }

  if (!supportAccessRightsData) {
    return <LoadingScreen />;
  }

  if (
    supportAccessRightsData.tokenExpired ||
    supportAccessRightsData.state === 'REVOKED' ||
    modifyRightsData?.state === 'REVOKED'
  ) {
    return (
      <Container>
        <Title>{t('support_access_rights_link_expired_title')}</Title>
        <Paragraph>{t('support_access_rights_link_expired_text')}</Paragraph>
      </Container>
    );
  }

  if (
    modifyRightsData?.state === 'GRANTED' ||
    supportAccessRightsData.state === 'GRANTED'
  ) {
    return (
      <Container>
        <Title>{t('support_access_rights_access_granted_title')}</Title>
        <Paragraph>
          {t('support_access_rights_access_granted_text', {
            email: <Email>{supportAccessRightsData.requesterEmail}</Email>,
            account: (chunks: ReactNode) => <Link to="/profile">{chunks}</Link>,
          })}
        </Paragraph>
      </Container>
    );
  }

  if (
    modifyRightsData?.state === 'REJECTED' ||
    supportAccessRightsData.state === 'REJECTED'
  ) {
    return (
      <Container>
        <Title>{t('support_access_rights_access_denied_title')}</Title>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('support_access_rights_title')}</Title>
      <Paragraph>
        {t('support_access_rights_paragraph_1', {
          email: <Email>{supportAccessRightsData.email}</Email>,
          requesterEmail: (
            <Email>{supportAccessRightsData.requesterEmail}</Email>
          ),
        })}
      </Paragraph>
      <Paragraph>{t('support_access_rights_paragraph_2')}</Paragraph>
      <Paragraph>
        {t('support_access_rights_paragraph_3', {
          account: (chunks: ReactNode) => <Link to="/profile">{chunks}</Link>,
        })}
      </Paragraph>
      <CheckboxLabel>
        <CheckboxContainer>
          <Checkbox
            checked={consentChecked}
            data-testid="marketing-checkbox"
            iconStyle={{ position: 'relative', top: -2 }}
            onChange={() => {
              setConsentChecked((checked) => !checked);
            }}
          />
        </CheckboxContainer>
        <div>{supportAccessRightsData.consentText}</div>
      </CheckboxLabel>
      <Paragraph>
        {t('support_access_rights_disclaimer', {
          pp: (chunks: ReactNode) => (
            <a href={privacyPolicyURL(selectedLocale)}>{chunks}</a>
          ),
          pt: (chunks: ReactNode) => (
            <a href="mailto:privacyteam@ouraring.com">{chunks}</a>
          ),
        })}
      </Paragraph>
      <RejectButton
        size="xsmall"
        rounded
        type="submit"
        onClick={() => {
          modifyRights({
            token,
            acceptAccess: false,
          });
        }}
      >
        {t('support_access_rights_reject_button')}
      </RejectButton>
      <ApproveButton
        size="xsmall"
        rounded
        type="submit"
        disabled={!consentChecked}
        onClick={() => {
          modifyRights({
            token,
            acceptAccess: true,
          });
        }}
      >
        {t('support_access_rights_approve_button')}
      </ApproveButton>
      {modifyRightsError && <Error>{modifyRightsError.message}</Error>}
    </Container>
  );
}
